import { motion } from "motion/react";
import { ReactNode } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { isTestMode } from "@/lib/testMode";

interface Props {
  children: ReactNode;
  className?: string;
  delay?: number;
  blur?: boolean;
  /** Gentle mode: less movement, longer duration — for trust-building & emotional sections */
  gentle?: boolean;
}

// Cinematic easing — smooth deceleration, unified across all reveals
const EASE = [0.22, 0.68, 0.35, 1] as const;

const AnimatedSection = ({ children, className = "", delay = 0, blur = true, gentle = false }: Props) => {
  const prefersReducedMotion = useReducedMotion();
  // Test-modus rendert net als reduced motion: direct in eindstaat,
  // zodat Playwright snapshots niet wachten op fade-in/blur transitions.
  const skipMotion = prefersReducedMotion || isTestMode();

  // Reduced-motion / test-modus: render direct in eindstaat zonder
  // transitie/blur/translate. We blijven motion.div gebruiken zodat de
  // DOM-structuur identiek blijft.
  if (skipMotion) {
    return (
      <motion.div initial={false} animate={{ opacity: 1 }} className={className}>
        {children}
      </motion.div>
    );
  }

  // Unified motion language: gentle uses less distance, slightly longer duration
  const yOffset = gentle ? 14 : 20;
  const duration = gentle ? 1.2 : 1.0;
  const blurAmount = gentle ? "blur(2px)" : "blur(4px)";

  return (
    <motion.div
      initial={{ opacity: 0, y: yOffset, ...(blur ? { filter: blurAmount } : {}) }}
      whileInView={{ opacity: 1, y: 0, ...(blur ? { filter: "blur(0px)" } : {}) }}
      viewport={{ once: true, margin: gentle ? "-35px" : "-50px" }}
      transition={{ duration, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default AnimatedSection;
